import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { FlaskConical, ListChecks, Plus, Radar } from "lucide-react";
import { PageHeader } from "@/components/domain/misc";
import { Page } from "@/components/layout/page";
import { RouteTabs } from "@/components/layout/route-tabs";
import { Button } from "@/components/ui/button";
import { q } from "@/lib/api";

export const Route = createFileRoute("/_app/signals")({
  component: SignalsLayout,
});

function TabCount({ n, label, tone = "muted" }: { n: number; label: string; tone?: "accent" | "muted" }) {
  if (n <= 0) return null;
  return (
    <span
      className={
        tone === "accent"
          ? "ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold tabular text-accent-fg"
          : "ml-0.5 rounded-full bg-surface-3 px-1.5 text-[10.5px] font-semibold leading-4 text-fg-2 tabular"
      }
      aria-label={label}
    >
      {n}
    </span>
  );
}

function SignalsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname.replace(/\/$/, "") });
  // The signal wizard and definition detail are focused pages with their own header and breadcrumb.
  const focused = pathname.startsWith("/signals/studio/");
  const proposals = useQuery(q.signalDefinitions({ status: "proposed" }));
  const pending = useQuery(q.reviewQueue({ labeled: false }));
  const nProposals = proposals.data?.length ?? 0;
  const nPending = pending.data?.length ?? 0;

  if (focused) return <Outlet />;

  return (
    <Page>
      <PageHeader
        title="Signals"
        description="Detectors running over every session and trace. Risk signals are curated by traces.insure and feed the reliability score; analytics signals are yours to define, backtest and tune, and never move price."
        actions={
          <Button size="sm" asChild>
            <Link to="/signals/studio/new">
              <Plus /> New signal
            </Link>
          </Button>
        }
      />
      <RouteTabs
        tabs={[
          { href: "/signals", label: "Explorer", icon: <Radar />, exact: true },
          { href: "/signals/studio", label: "Studio", icon: <FlaskConical />, badge: <TabCount n={nProposals} tone="accent" label={`${nProposals} new proposals`} /> },
          { href: "/signals/review", label: "Review queue", icon: <ListChecks />, badge: <TabCount n={nPending} label={`${nPending} hits awaiting review`} /> },
        ]}
      />
      <Outlet />
    </Page>
  );
}
